import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { Input } from "@/components/ui/input";

export const VERDICT = {
  verified: { label: "Verified", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  discrepant: { label: "Discrepant", cls: "bg-red-50 text-red-700 border-red-200" },
  unverifiable: { label: "Unverifiable", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};
export const FIELD_STATUS = { unassigned: "Not yet assigned", assigned: "Assigned to agent", attempted: "1 attempt, no contact", completed: "Visit completed", no_contact: "No contact after 2 attempts" };
const WEIGHT_CLS = { supports: "text-emerald-700", conflicts: "text-red-700", neutral: "text-slate-500" };

export const Box = ({ title, children, testid }) => (
  <section className="rounded-2xl border border-gray-200 bg-white p-5" data-testid={testid}>
    <h2 className="font-mono-label text-[10px] font-semibold text-oath-muted">{title}</h2>
    <div className="mt-3">{children}</div>
  </section>
);

export const VerdictBadge = ({ v }) => (
  <span className={`inline-flex rounded-full border px-3 py-1 font-mono-label text-[10px] font-semibold ${VERDICT[v]?.cls}`} data-testid="verdict-badge">{VERDICT[v]?.label.toUpperCase()}</span>
);

export const EvidenceList = ({ items = [] }) => (
  <ul className="grid gap-2" data-testid="evidence-list">
    {items.length === 0 && <li className="text-sm text-oath-muted">No evidence recorded yet.</li>}
    {items.map((e) => (
      <li key={e.id} className="rounded-xl bg-oath-bg/60 p-3 text-sm">
        <div className="flex flex-wrap justify-between gap-2"><b className="text-oath-ink">{e.source}</b><span className={`font-mono-label text-[9px] ${WEIGHT_CLS[e.weight]}`}>{e.weight.toUpperCase()}</span></div>
        <p className="mt-1 text-oath-secondary">{e.finding}</p>
        <div className="mt-1 font-mono text-[10px] text-oath-muted">{e.by} · {fmtDate(e.at)}</div>
      </li>
    ))}
  </ul>
);

const ConsentUpload = ({ c, onDone }) => {
  const [file, setFile] = useState(null);
  const [on, setOn] = useState("");
  const [how, setHow] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append("file", file);
    fd.append("collected_on", on);
    fd.append("collected_how", how);
    setBusy(true);
    try {
      await api.post(`/cases/${c.id}/consent-upload`, fd);
      toast.success("Consent form uploaded");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={send} className="grid gap-3" data-testid="consent-upload-form">
      <p className="text-sm text-oath-secondary">Upload the subject's signed consent form. By uploading, you attest it was collected for this case and purpose.</p>
      <input type="file" accept="image/*,application/pdf" required onChange={(e) => setFile(e.target.files[0])} data-testid="consent-upload-file" className="text-sm" />
      <Input type="date" required value={on} onChange={(e) => setOn(e.target.value)} data-testid="consent-upload-date" />
      <Input required placeholder="Where and how consent was collected (e.g. signed application form)" value={how} onChange={(e) => setHow(e.target.value)} data-testid="consent-upload-how" />
      <button disabled={busy} className="w-fit rounded-full bg-oath-ink px-5 py-2.5 font-mono-label text-[10px] font-semibold text-white disabled:opacity-60" data-testid="consent-upload-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "UPLOAD & ATTEST"}
      </button>
    </form>
  );
};

export const CaseWork = ({ c, canOrder, onChanged }) => {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get(`/cases/${c.id}/work`).then(({ data }) => setDocs(data.documents)).catch(() => setDocs([]));
  }, [c.id, c.status]);
  return (
    <>
      {c.status === "awaiting_consent_upload" && canOrder && <Box title="UPLOAD SIGNED CONSENT" testid="case-consent-upload"><ConsentUpload c={c} onDone={onChanged} /></Box>}
      <Box title="RESULT" testid="case-result">
        {c.status === "delivered" && c.verdict ? (
          <>
            <VerdictBadge v={c.verdict.verdict} />
            <p className="mt-3 text-sm text-oath-ink" data-testid="case-verdict-summary">{c.verdict.summary}</p>
            <div className="mt-4"><EvidenceList items={c.evidence} /></div>
          </>
        ) : <p className="text-sm text-oath-muted" data-testid="case-result-pending">The result appears here once our analysts finish and QA approves it.</p>}
      </Box>
      <Box title="CHECK TYPE" testid="case-check-type">
        <p className="text-sm text-oath-ink">{c.check_type === "records_field" ? "Records + field visit" : "Records only"}</p>
        {c.field && <p className="mt-1 text-sm text-oath-secondary" data-testid="case-field-status">Field visit ({c.field.city}): {FIELD_STATUS[c.field.status]}</p>}
        {docs.length > 0 && <p className="mt-1 text-sm text-oath-secondary" data-testid="case-docs-summary">Accepted documents from the subject: {docs.map((d) => d.doc_type).join(", ")}</p>}
      </Box>
    </>
  );
};
